import { useState } from "react";
import {
  SURF_PRESENTATION_TYPES,
  polishOutlineSections,
  splitSurfOutline,
  type SurfOutlineSection,
} from "@vida/surf-core";
import { createSurfApiConfig, ensureSurfClientConfig } from "../../services/surfClientConfig";
import { OutlineSectionsEditor } from "../outline/OutlineSectionsEditor";

type SlideTarget = number | "auto";

type Props = {
  sourceText: string;
  sections: SurfOutlineSection[];
  presentationType: string;
  slideTarget: SlideTarget;
  onSectionsChange: (sections: SurfOutlineSection[]) => void;
  onPresentationTypeChange: (type: string) => void;
  onSlideTargetChange: (target: SlideTarget) => void;
};

export function OutlineStep({
  sourceText,
  sections,
  presentationType,
  slideTarget,
  onSectionsChange,
  onPresentationTypeChange,
  onSlideTargetChange,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slideTargetAuto, setSlideTargetAuto] = useState(slideTarget === "auto");
  const [slideTargetNumber, setSlideTargetNumber] = useState(
    typeof slideTarget === "number" ? slideTarget : 8,
  );

  const resolvedSlideTarget: SlideTarget = slideTargetAuto ? "auto" : slideTargetNumber;

  const applySlideTarget = (auto: boolean, num: number) => {
    setSlideTargetAuto(auto);
    setSlideTargetNumber(num);
    onSlideTargetChange(auto ? "auto" : num);
  };

  return (
    <section className="wizard-panel">
      <h2>Outline</h2>
      <p className="muted">
        Genera e modifica sezioni ricche (bullet, visual hint, layout) prima di creare le slide.
      </p>

      <div className="outline-fields-row">
        <label className="field-label">
          Tipo presentazione
          <select
            value={presentationType}
            onChange={(e) => onPresentationTypeChange(e.target.value)}
          >
            <option value="">— automatico —</option>
            {SURF_PRESENTATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <label className="field-label">
          Numero slide
          <div className="outline-slide-target">
            <label className="outline-slide-target-auto">
              <input
                type="checkbox"
                checked={slideTargetAuto}
                onChange={(e) => applySlideTarget(e.target.checked, slideTargetNumber)}
              />
              Auto
            </label>
            <input
              type="number"
              className="text-input"
              min={1}
              max={40}
              disabled={slideTargetAuto}
              value={slideTargetNumber}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n)) return;
                applySlideTarget(false, n);
              }}
            />
          </div>
          <span className="muted outline-slide-target-hint">
            Auto sceglie in base al contenuto; un numero forza il totale.
          </span>
        </label>
      </div>

      <div className="wizard-actions">
        <button
          type="button"
          className="primary-btn"
          disabled={busy || !sourceText.trim()}
          onClick={() =>
            void (async () => {
              setError(null);
              setBusy(true);
              try {
                await ensureSurfClientConfig();
                const parsed = await splitSurfOutline(
                  createSurfApiConfig(),
                  sourceText,
                  presentationType || undefined,
                  resolvedSlideTarget,
                );
                onSectionsChange(parsed);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            })()
          }
        >
          Genera outline (AI)
        </button>
        <button
          type="button"
          disabled={busy || sections.length === 0}
          onClick={() =>
            void (async () => {
              setError(null);
              setBusy(true);
              try {
                await ensureSurfClientConfig();
                const polished = await polishOutlineSections(
                  createSurfApiConfig(),
                  sections,
                );
                onSectionsChange(polished);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            })()
          }
        >
          Rifinisci contenuto (AI)
        </button>
        {busy && <span className="muted">Job async in corso…</span>}
      </div>

      {error && <p className="error">{error}</p>}

      <OutlineSectionsEditor sections={sections} onSectionsChange={onSectionsChange} />
    </section>
  );
}
